import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import { formatMoneyColumnHeader } from "@common/helpers/number";
import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { ViewConfig } from "@lib/views";
import { AMOUNT_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import {
    ArAgingDetailAmountCell,
    ArAgingDetailClassCell,
    ArAgingDetailCustomerCell,
    ArAgingDetailDescriptionCell,
    ArAgingDetailDueDateCell,
    ArAgingDetailEntityCell,
    ArAgingDetailNameCell,
    ArAgingDetailOpenCell,
    ArAgingDetailReferenceCell,
    ArAgingDetailSourceCell,
} from "./arAgingDetailCells";
import { ArAgingDetailViewRow } from "./arAgingDetailViewTypes";

export enum ArAgingDetailColumn {
    NAME = "name",
    SOURCE = "source",
    REFERENCE = "reference",
    CUSTOMER = "customer",
    DESCRIPTION = "description",
    CLASS = "class",
    ENTITY = "entity",
    DUE_DATE = "dueDate",
    AMOUNT = "amount",
    OPEN = "open",
}

const DATE_MIN_SIZE = 120;
const SOURCE_MIN_SIZE = 100;
const REFERENCE_MIN_SIZE = 140;
const CUSTOMER_MIN_SIZE = 180;
const DESCRIPTION_MIN_SIZE = 200;
const CLASS_MIN_SIZE = 120;
const ENTITY_MIN_SIZE = 140;
const DEFAULT_MAX_SIZE = 400;

/**
 * Builds the ViewConfig for the A/R Aging Detail table: a bucket-nested
 * table (bucket parent rows wrapping invoice line children, plus a trailing
 * grand total row), mirroring the general ledger / elimination detail
 * nesting convention. The Entity column only renders for multi-entity
 * queries (`showEntityColumn` from the backend response), mirroring how the
 * general ledger conditionally shows its Entity column via
 * `buildGLConfig({ multiEntity })`.
 */
export function buildArAgingDetailConfig(opts: {
    showEntityColumn: boolean;
    numberFormat?: NumberFormatValues;
}): ViewConfig<ArAgingDetailViewRow, ArAgingDetailColumn, never> {
    return {
        columns: {
            [ArAgingDetailColumn.NAME]: {
                name: "Bucket / Date",
                header: "Date",
                accessorKey: "rowKind",
                cell: ArAgingDetailNameCell,
                minSize: 300,
                maxSize: 480,
                immutable: true,
            },
            [ArAgingDetailColumn.SOURCE]: {
                name: "Source",
                header: "Source",
                accessorKey: "rowKind",
                cell: ArAgingDetailSourceCell,
                minSize: SOURCE_MIN_SIZE,
                maxSize: DEFAULT_MAX_SIZE,
            },
            [ArAgingDetailColumn.REFERENCE]: {
                name: "Reference",
                header: "Reference",
                accessorKey: "rowKind",
                cell: ArAgingDetailReferenceCell,
                minSize: REFERENCE_MIN_SIZE,
                maxSize: DEFAULT_MAX_SIZE,
            },
            [ArAgingDetailColumn.CUSTOMER]: {
                name: "Customer",
                header: "Customer",
                accessorKey: "rowKind",
                cell: ArAgingDetailCustomerCell,
                minSize: CUSTOMER_MIN_SIZE,
                maxSize: DEFAULT_MAX_SIZE,
            },
            [ArAgingDetailColumn.DESCRIPTION]: {
                name: "Description",
                header: "Description",
                accessorKey: "rowKind",
                cell: ArAgingDetailDescriptionCell,
                minSize: DESCRIPTION_MIN_SIZE,
                maxSize: DEFAULT_MAX_SIZE,
            },
            [ArAgingDetailColumn.CLASS]: {
                name: "Class",
                header: "Class",
                accessorKey: "rowKind",
                cell: ArAgingDetailClassCell,
                minSize: CLASS_MIN_SIZE,
                maxSize: DEFAULT_MAX_SIZE,
            },
            [ArAgingDetailColumn.ENTITY]: {
                name: "Entity",
                header: "Entity",
                accessorKey: "rowKind",
                cell: ArAgingDetailEntityCell,
                minSize: ENTITY_MIN_SIZE,
                maxSize: DEFAULT_MAX_SIZE,
                hiddenFromSettings: !opts.showEntityColumn,
            },
            [ArAgingDetailColumn.DUE_DATE]: {
                name: "Due date",
                header: "Due date",
                accessorKey: "rowKind",
                cell: ArAgingDetailDueDateCell,
                minSize: DATE_MIN_SIZE,
                maxSize: DATE_MIN_SIZE,
            },
            [ArAgingDetailColumn.AMOUNT]: {
                name: "Amount",
                header: formatMoneyColumnHeader("Amount", opts.numberFormat),
                accessorKey: "rowKind",
                cell: ArAgingDetailAmountCell,
                ...AMOUNT_COLUMN_SIZE,
            },
            [ArAgingDetailColumn.OPEN]: {
                name: "Open",
                header: formatMoneyColumnHeader("Open", opts.numberFormat),
                accessorKey: "rowKind",
                cell: ArAgingDetailOpenCell,
                ...AMOUNT_COLUMN_SIZE,
                immutable: true,
            },
        },
        nesting: {
            enabled: true,
            expandColumn: ArAgingDetailColumn.NAME,
            childrenKey: "children",
        },
    };
}

export function buildArAgingDetailView(opts: {
    showEntityColumn: boolean;
}): View {
    return {
        type: ViewType.AR_AGING_DETAIL,
        columns: [
            { id: ArAgingDetailColumn.NAME, options: { pin: "left" as const } },
            { id: ArAgingDetailColumn.SOURCE },
            { id: ArAgingDetailColumn.REFERENCE },
            { id: ArAgingDetailColumn.CUSTOMER },
            { id: ArAgingDetailColumn.DESCRIPTION },
            { id: ArAgingDetailColumn.CLASS },
            ...(opts.showEntityColumn
                ? [{ id: ArAgingDetailColumn.ENTITY }]
                : []),
            { id: ArAgingDetailColumn.DUE_DATE },
            {
                id: ArAgingDetailColumn.AMOUNT,
                options: { pin: "right" as const },
            },
            {
                id: ArAgingDetailColumn.OPEN,
                options: { pin: "right" as const },
            },
        ],
        filters: {},
        options: {
            selection: false,
            bulkActions: false,
            density: ViewDensity.LG,
        },
    };
}
